<template>
  <color-picker
    v-if="editorMeta.editor === 'color'"
    :pureColor="modelValue || '#ffffff'"
    @update:pureColor="$emit('update:modelValue', $event)"
    useType="pure" format="hex" lang="En" />

  <BuilderCheckbox
    v-else-if="editorMeta.editor === 'bool'"
    :modelValue="isTruthy(modelValue)"
    @update:modelValue="$emit('update:modelValue', $event ? '1' : '0')" />

  <BuilderInput
    v-else-if="editorMeta.editor === 'number'"
    type="number" class="w-full text-sm px-2 py-1"
    :min="editorMeta.min" :max="editorMeta.max"
    :placeholder="placeholder"
    :modelValue="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)" />

  <BuilderSelect
    v-else-if="editorMeta.editor === 'enum'"
    class="w-full text-sm px-1 py-1"
    :modelValue="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)">
    <option value="">—</option>
    <option v-for="v in editorMeta.values" :key="v" :value="v">{{ v }}</option>
  </BuilderSelect>

  <ResourcePicker
    v-else-if="editorMeta.editor === 'font'"
    resourceType="font"
    :modelValue="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)" />

  <ResourcePicker
    v-else-if="editorMeta.editor === 'image'"
    resourceType="image"
    :modelValue="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)" />

  <StringBuilderField
    v-else-if="stringBuilder"
    :placeholder="placeholder"
    :variables="builderVariables"
    :valueExpression="valueExpression"
    :modelValue="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)" />

  <BuilderInput
    v-else
    type="text" class="w-full text-sm px-2 py-1"
    :placeholder="placeholder"
    :modelValue="modelValue"
    @update:modelValue="$emit('update:modelValue', $event)" />
</template>

<script setup>
/**
 * PropertyEditorFactory.vue
 *
 * Renders the typed control for a single literal value, selected entirely by
 * `editorMeta.editor` (from automation/schema/PropertyEditorMeta.js). Callers
 * never branch on property name — adding a new editor kind only requires a
 * new branch here plus a matching 'editor' string from the schema.
 *
 * The free-text branch has two forms: a bare input, or (when the caller
 * passes `stringBuilder`) StringBuilderField.vue, which adds the Designer's
 * existing String Builder — icons and variables — to the same input. Which
 * one a value gets is still decided by the schema: `stringBuilder` only has
 * an effect where the schema already resolved to a free-text editor, so a
 * caller can ask for it uniformly without knowing the property's type.
 *
 * Used only within the Home Assistant builder dialogs (binding editors,
 * ConditionEditor's right operand) — never by the main property panels —
 * so its scalar controls are BuilderCheckbox/BuilderInput/BuilderSelect,
 * not the global form-checkbox/form-input/form-select (see
 * BuilderSelect.vue's doc comment for why those don't fit here).
 */
import ResourcePicker from '../../resources/ResourcePicker.vue';
import BuilderCheckbox from './BuilderCheckbox.vue';
import BuilderInput from './BuilderInput.vue';
import BuilderSelect from './BuilderSelect.vue';
import StringBuilderField from './StringBuilderField.vue';

const props = defineProps({
  editorMeta: { type: Object, required: true }, // { editor, values?, min?, max?, default? }
  modelValue: { type: [String, Number], default: '' },
  // Hint text for the free-text/number editors only — the pickers and the
  // dropdown label themselves. Optional; callers that don't set it get the
  // same bare controls as before.
  placeholder: { type: String, default: '' },
  // Offer the Designer's String Builder (icons + variables, see
  // StringBuilderField.vue) alongside free-text entry. Only reachable on
  // the free-text branch, i.e. only for a property the schema types as a
  // string — the typed editors (color/bool/number/enum/font/image) are a
  // composed string's wrong shape and ignore this. Default off, so every
  // existing caller renders exactly the control it rendered before.
  stringBuilder: { type: Boolean, default: false },
  // The complete Variables list the String Builder should offer — see
  // StringBuilderField.vue. Caller-supplied because only the caller knows the
  // value's context (for a mapping result: the bound entity's own value).
  // Defaults to none rather than to the plate's runtime placeholders.
  builderVariables: { type: Array, default: () => [] },
  // The Jinja expression `{{value}}` stands for in this value's context, from
  // buildIncomingValueExpression(). Same reason as builderVariables: the
  // caller owns the context, this factory only forwards it.
  valueExpression: { type: String, default: '' },
});
defineEmits(['update:modelValue']);

function isTruthy(v) {
  return v === true || v === '1' || v === 1 || v === 'true';
}
</script>
